import { twMerge } from "tailwind-merge";
import { ChartDatePickerInput, EventCards, EventsTable, SaleAndTicketChart, TotalDataSlider } from "./components";
import Select from "components/select";
import { EVENT_STATUS_OPTIONS, GATEWAY_OWNER_OPTIONS, TICKET_PLATFORMS } from "utills/constants";
import Pagination from "components/pagination";
import { TooltipCircle } from "components/tooltipCircle";
import { DatePickerInput } from "components/datePickerInput";
import { HttpError, useList, useOne } from "@refinedev/core";
import { useEffect, useMemo, useState } from "react";
import { DatePickerFilterOption, IGatewayFilterOptions, IOrgEventIdsItem, IOrgIdsItem, IOverviewEvents } from "interfaces";
import moment from "moment";
import { FullScreenLoading } from "components/fullScreenLoading";
import { Label } from "components/label";

const ALL_OPTION = {label: "All", value: "all"};

export const Overview: React.FC = () => {
  const [trigger, setTrigger] = useState(false);
  const [dateOption, setDateOption] = useState(DatePickerFilterOption.YEAR);
  // const [chartDateOption, setChartDateOption] = useState(currentFilterValues.chart_date_type);

  const [filters, setFilters] = useState([
    { field: "organization_id", operator: "eq", value: "all" },
    { field: "event_id", operator: "eq", value: "all" },
    { field: "from_date", operator: "eq", value: moment().startOf("year").format("YYYY-MM-DD") },
    { field: "to_date", operator: "eq", value: moment().endOf("year").format("YYYY-MM-DD") },
    { field: "event_status", operator: "eq", value: "all" },
    { field: "page", operator: "eq", value: "1" },
    { field: "order", operator: "eq", value: "" },
    { field: "sort_by", operator: "eq", value: "" },
    { field: "chart_organization_id", operator: "eq", value: "all" },
    { field: "chart_date_type", operator: "eq", value: DatePickerFilterOption.YEAR },
    { field: "chart_date_value", operator: "eq", value: moment().year() },
    { field: "chart_ticket_platform", operator: "eq", value: "" },
    { field: "gateway_owner", operator: "eq", value: "all" },
    { field: "gateway_partner_code", operator: "eq", value: "" },
    { field: "gateway_name", operator: "eq", value: "" },
    { field: "chart_gateway_owner", operator: "eq", value: "all" },
    { field: "chart_gateway_partner_code", operator: "eq", value: "" },
    { field: "chart_gateway_name", operator: "eq", value: "" },
  ])

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : [],
    );

    return {
      event_id: logicalFilters.find((item) => item.field === "event_id")?.value ?? "",
      from_date: logicalFilters.find((item) => item.field === "from_date")?.value ?? "",
      to_date: logicalFilters.find((item) => item.field === "to_date")?.value ?? "",
      organization_id: logicalFilters.find((item) => item.field === "organization_id")?.value ?? "",
      event_status: logicalFilters.find((item) => item.field === "event_status")?.value ?? "",
      page: logicalFilters.find((item) => item.field === "page")?.value ?? "1",
      chart_organization_id: logicalFilters.find((item) => item.field === "chart_organization_id")?.value ?? "",
      chart_date_type: logicalFilters.find((item) => item.field === "chart_date_type")?.value ?? "",
      chart_date_value: logicalFilters.find((item) => item.field === "chart_date_value")?.value ?? "",
      chart_ticket_platform: logicalFilters.find((item) => item.field === "chart_ticket_platform")?.value ?? "",
      order: logicalFilters.find((item) => item.field === "order")?.value ?? "",
      sort_by: logicalFilters.find((item) => item.field === "sort_by")?.value ?? "",
      ticket_platform: logicalFilters.find((item) => item.field === "ticket_platform")?.value ?? "",
      gateway_owner: logicalFilters.find((item) => item.field === "gateway_owner")?.value ?? "",
      gateway_partner_code: logicalFilters.find((item) => item.field === "gateway_partner_code")?.value ?? "",
      gateway_name: logicalFilters.find((item) => item.field === "gateway_name")?.value ?? "",
      chart_gateway_owner: logicalFilters.find((item) => item.field === "chart_gateway_owner")?.value ?? "",
      chart_gateway_partner_code: logicalFilters.find((item) => item.field === "chart_gateway_partner_code")?.value ?? "",
      chart_gateway_name: logicalFilters.find((item) => item.field === "chart_gateway_name")?.value ?? "",
    };
  }, [filters]);

  const {
    data: saleData,
    refetch,
    isLoading,
    isFetching
  } = useList<IOverviewEvents>({
    resource: "dashboard/sale",
    pagination: {
      mode: "off"
    },
    meta: {
      resource: "data",
      filters: [
        { field: "organization_id", operator: "eq", value: currentFilterValues.organization_id },
        { field: "event_id", operator: "eq", value: currentFilterValues.event_id },
        { field: "from_date", operator: "eq", value: currentFilterValues.from_date },
        { field: "to_date", operator: "eq", value: currentFilterValues.to_date },
        { field: "event_status", operator: "eq", value: currentFilterValues.event_status },
        { field: "order", operator: "eq", value: currentFilterValues.order },
        { field: "sort_by", operator: "eq", value: currentFilterValues.sort_by },
        { field: "page", operator: "eq", value: currentFilterValues.page },
        { field: "ticket_platform", operator: "eq", value: currentFilterValues.ticket_platform },
        { field: "gateway_owner", operator: "eq", value: currentFilterValues.gateway_owner },
        { field: "gateway_partner_code", operator: "eq", value: currentFilterValues.gateway_partner_code },
        { field: "gateway_name", operator: "eq", value: currentFilterValues.gateway_name },
        { field: "currency", operator: "eq", value: localStorage.getItem("currency") },
      ],
    },
  });

  const { data: orgIds, isLoading: orgIdsLoading } = useOne<IOrgIdsItem[], HttpError>({
    resource: "dashboard/organization-filter-options",
    id: "",
    meta: {
      resource: "data",
      filters: [
        { field: "gateway_owner", operator: "eq", value: currentFilterValues.gateway_owner },
        { field: "gateway_partner_code", operator: "eq", value: currentFilterValues.gateway_partner_code },
        { field: "gateway_name", operator: "eq", value: currentFilterValues.gateway_name },
        { field: "ticket_platform", operator: "eq", value: currentFilterValues.ticket_platform },
      ]
    }
  });

  const { data: gatewayFilterOptions } = useOne<IGatewayFilterOptions, HttpError>({
    resource: "dashboard/gateway-filter-options",
    id: "",
    meta: {
      resource: "data",
      filters: [
        { field: "gateway_owner", operator: "eq", value: currentFilterValues.gateway_owner },
        { field: "organization_id", operator: "eq", value: currentFilterValues.organization_id },
        { field: "ticket_platform", operator: "eq", value: currentFilterValues.ticket_platform },
      ]
    },
  });

  const { data: chartOrgIds } = useOne<IOrgIdsItem[], HttpError>({
    resource: "dashboard/organization-filter-options",
    id: "",
    meta: {
      resource: "data",
      filters: [
        { field: "gateway_owner", operator: "eq", value: currentFilterValues.chart_gateway_owner },
        { field: "gateway_partner_code", operator: "eq", value: currentFilterValues.chart_gateway_partner_code },
        { field: "gateway_name", operator: "eq", value: currentFilterValues.chart_gateway_name },
        { field: "ticket_platform", operator: "eq", value: currentFilterValues.chart_ticket_platform },
      ]
    }
  });

  const { data: chartGatewayFilterOptions } = useOne<IGatewayFilterOptions, HttpError>({
    resource: "dashboard/gateway-filter-options",
    id: "",
    meta: {
      resource: "data",
      filters: [
        { field: "gateway_owner", operator: "eq", value: currentFilterValues.chart_gateway_owner },
        { field: "organization_id", operator: "eq", value: currentFilterValues.chart_organization_id },
        { field: "ticket_platform", operator: "eq", value: currentFilterValues.chart_ticket_platform },
      ]
    },
  });

  const { data: organizationEventsData } = useOne<IOrgEventIdsItem[], HttpError>({
    resource: "organization",
    id: `${currentFilterValues?.organization_id}/events/dashboard`,
    meta: {
      resource: "data",
      ...(currentFilterValues.gateway_owner && currentFilterValues.gateway_owner !== "actiup" && {
        filters: [{ field: "gateway_owner", operator: "eq", value: currentFilterValues.gateway_owner }]
      })
    },
    queryOptions: {
      enabled: !!currentFilterValues.organization_id,
    } as any,
  });

  const organizationEventIds = ((organizationEventsData?.data ?? []).length > 0 ? [ALL_OPTION] : []).concat((organizationEventsData?.data ?? []).map((event: IOrgEventIdsItem) => (
    { value: event.id, label: event.name_en }
  )));

  const organizationIds = ((orgIds?.data ?? []).length > 0 ? [ALL_OPTION] : []).concat((orgIds?.data ?? []).map((event: IOrgIdsItem) => (
    { value: event.id, label: event.name_en }
  )));

  const partnerCodeOptions = ([{ label: "All", value: "" }]).concat(
    (gatewayFilterOptions?.data?.partner_codes ?? []).map((code: string) => ({ label: code, value: code }))
  );

  const gatewayNameOptions = ([{ label: "All", value: "" }]).concat(
    (gatewayFilterOptions?.data?.gateway_names ?? []).map((name: string) => ({ label: name, value: name }))
  );

  const chartOrganizationIds = ((chartOrgIds?.data ?? []).length > 0 ? [ALL_OPTION] : []).concat((chartOrgIds?.data ?? []).map((org: IOrgIdsItem) => (
    { value: org.id, label: org.name_en }
  )));

  const chartPartnerCodeOptions = ([{ label: "All", value: "" }]).concat(
    (chartGatewayFilterOptions?.data?.partner_codes ?? []).map((code: string) => ({ label: code, value: code }))
  );

  const chartGatewayNameOptions = ([{ label: "All", value: "" }]).concat(
    (chartGatewayFilterOptions?.data?.gateway_names ?? []).map((name: string) => ({ label: name, value: name }))
  );

  const ticketPlatformList = ([{label: "All", value: ""}]).concat(TICKET_PLATFORMS ?? []);
  
  
  const CHART_FILTER_KEYS = new Set([
    "chart_organization_id",
    "chart_date_type",
    "chart_date_value",
    "chart_ticket_platform",
    "chart_gateway_owner",
    "chart_gateway_partner_code",
    "chart_gateway_name",
  ]);

  const _handleFilterChange = (key: string, value: any) => {
    if (!trigger && !CHART_FILTER_KEYS.has(key)) {
      setTrigger(true);
    }
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const fieldIndex = updatedFilters.findIndex(filter => filter.field === key);
    if (fieldIndex < 0) {
      updatedFilters.push({field: key, operator: "eq", value: value})
    } else {
      updatedFilters[fieldIndex].value = value;
    }

    if (key !== "page") {
      const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
      if (pageIndex < 0) {
        updatedFilters.push({field: "page", operator: "eq", value: "1"})
      } else {
        updatedFilters[pageIndex].value = "1";
      }  
    }

    setFilters(updatedFilters);
  }

  const _handleDateChange = (dates: [Date, Date]) => {
    if (!trigger) {
      setTrigger(true);
    }

    const [start, end] = dates;
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const fromDateIndex = updatedFilters.findIndex(filter => filter.field === "from_date");
    const endDateIndex = updatedFilters.findIndex(filter => filter.field === "to_date");
    fromDateIndex < 0 ? updatedFilters.push({field: "from_date", operator: "eq", value: moment(start).format("YYYY-MM-DD")}) : updatedFilters[fromDateIndex].value = moment(start).format("YYYY-MM-DD");
    endDateIndex < 0 ? updatedFilters.push({field: "to_date", operator: "eq", value: moment(end).format("YYYY-MM-DD")}) : updatedFilters[endDateIndex].value =moment(end).format("YYYY-MM-DD");
    setFilters(updatedFilters);
  }

  const _handleChartDateChange = (date: string, type: DatePickerFilterOption) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const chartDateOptionIndex = updatedFilters.findIndex(filter => filter.field === "chart_date_type");
    const chartDateIndex = updatedFilters.findIndex(filter => filter.field === "chart_date_value");
    chartDateOptionIndex < 0 ? updatedFilters.push({field: "chart_date_type", operator: "eq", value: type}) : updatedFilters[chartDateOptionIndex].value = type;
    chartDateIndex < 0 ? updatedFilters.push({field: "chart_date_value", operator: "eq", value: date}) : updatedFilters[chartDateIndex].value = date;
    setFilters(updatedFilters);
  }

  useEffect(() => {
    if (trigger) {
      refetch();
      setTrigger(false);
    }
  }, [trigger, refetch]);

  const tableQueryResult = saleData;

  return (
    <div className="wrapper">
      <div className={twMerge("box-container", "bg-magnolia-700")}>
        <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-y-4 gap-x-2 md:gap-x-4 mb-4">
          <div className="flex-1">
            <Label id="gateway_owner" name="Gateway Owner" />
            <Select 
              className="py-0.5"
              options={GATEWAY_OWNER_OPTIONS}
              id="gateway_owner"
              placeholder="Select Gateway Owner"
              value={GATEWAY_OWNER_OPTIONS.find((selected) => selected.value === currentFilterValues.gateway_owner)}
              onChange={(selected: {label: string; value: string}) => {
                _handleFilterChange("gateway_owner", selected.value);
                _handleFilterChange("organization_id", "all");
                _handleFilterChange("event_id", "all");
                _handleFilterChange("ticket_platform", "");
                _handleFilterChange("gateway_partner_code", "");
                _handleFilterChange("gateway_name", "");
              }}
            />
          </div>
          <div className="flex-1">
            <Label id="gateway_partner_code" name="Gateway Partner Code" />
            <Select 
              key={`gateway_partner_code__${currentFilterValues.gateway_owner}__${currentFilterValues.organization_id}__${currentFilterValues.ticket_platform}`}
              className="py-0.5"
              options={partnerCodeOptions}
              id="gateway_partner_code"
              placeholder="Select Partner Code"
              value={partnerCodeOptions.find((selected) => selected.value === currentFilterValues.gateway_partner_code)}
              onChange={(selected: {label: string; value: string}) => {
                _handleFilterChange("gateway_partner_code", selected.value);
              }}
            />
          </div>
          <div className="flex-1">
            <Label id="gateway_name" name="Gateway Name" />
            <Select 
              key={`gateway_name__${currentFilterValues.gateway_owner}__${currentFilterValues.organization_id}__${currentFilterValues.ticket_platform}`}
              className="py-0.5"
              options={gatewayNameOptions}
              id="gateway_name"
              placeholder="Select Gateway Name"
              value={gatewayNameOptions.find((selected) => selected.value === currentFilterValues.gateway_name)}
              onChange={(selected: {label: string; value: string}) => {
                _handleFilterChange("gateway_name", selected.value);
              }}
            />
          </div>
          <div className="flex-1">
            <Label id="org_name" name="Organization" />
            <Select 
              className="py-0.5"
              options={organizationIds}
              isLoading={orgIdsLoading}
              id="org_name"
              placeholder="Select Organization"
              value={organizationIds.find((selected: {label: string; value: string}) => selected.value === currentFilterValues.organization_id)}
              onChange={(selected: {label: string; value: string}) => {
                _handleFilterChange("organization_id", selected.value);
                _handleFilterChange("event_id", "all");
                _handleFilterChange("gateway_partner_code", "");
                _handleFilterChange("gateway_name", "");
              }}
            />
          </div>
          {(currentFilterValues.gateway_owner === "all" || currentFilterValues.gateway_owner === "actiup") && (
            <div className="flex-1">
              <Label id="ticket_platform" name="Ticket Platform" />
              <Select 
                className="py-0.5"
                options={ticketPlatformList}
                id="ticket_platform"
                placeholder="Select Ticket Platform"
                value={ticketPlatformList.find((selected: {label: string; value: string}) => selected.value === currentFilterValues.ticket_platform)}
                onChange={(selected: {label: string; value: string}) => {
                  _handleFilterChange("ticket_platform", selected.value);
                  _handleFilterChange("gateway_partner_code", "");
                  _handleFilterChange("gateway_name", "");
                }}
              />
            </div>
          )}
          <div className="flex-1">
            <Label id="date_range" name="Date Range" />
            <div className="w-full bg-white rounded-lg">
              <DatePickerInput 
                defaultOption={dateOption}
                setDateOption={setDateOption}
                defaultStartDate={moment(currentFilterValues.from_date).toDate()} 
                defaultEndDate={moment(currentFilterValues.to_date).toDate()}
                _handleDateChange={_handleDateChange}
              />
            </div>
          </div>
        </div>
        <TotalDataSlider currentFilterValues={currentFilterValues} dateOption={dateOption} />
      </div>
      <div className="box-container p-0">
        <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">Events</h3>
          <div className="flex-[3] flex flex-col gap-y-3 gap-x-3 max-w-full sm:flex-row">
            <div className="flex-1 w-full max-w-full box-border flex flex-col justify-end gap-y-3 sm:flex-row sm:gap-x-3 sm:w-fit">
              <div className="flex-1 lg:flex-none lg:w-[500px] lg:max-w-full">
                <Select 
                  key={`event-id__${currentFilterValues.organization_id}`}
                  options={organizationEventIds}
                  id="event"
                  placeholder="Select Event"
                  className="py-0.5 w-full !rounded-md"
                  value={organizationEventIds.find((selected: {label: string; value: string}) => selected.value === currentFilterValues.event_id)}
                  onChange={(selected: {label: string; value: string}) => _handleFilterChange("event_id", selected.value)}      
                />
              </div>
              <div className="w-full shrink-0 sm:w-[210px] max-w-full">
                <Select 
                  options={EVENT_STATUS_OPTIONS} 
                  id="status"
                  placeholder="Select Status"
                  className="py-0.5 w-full !rounded-md max-w-full"
                  value={EVENT_STATUS_OPTIONS.find((d) => d.value === currentFilterValues.event_status)}
                  onChange={(selected: {label: string; value: string}) => _handleFilterChange("event_status", selected.value)}      
                />
              </div>
            </div>
            {/* <Button
              type="button"
              className="w-full shrink-0 px-3 py-1.5 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100 sm:w-fit"
            >
              <DownloadIcon className="w-4 mr-1.5" /> Export
            </Button> */}
          </div>
        </div>
        <div className="box-container">
        {/* {!tableQueryResult?.data?.length &&
        !isFetching && (
          <div className="text-center">
            No data
          </div>
        )} */}
          <div className="sm:hidden">
            <EventCards 
              data={tableQueryResult?.data}
              pageSize={tableQueryResult?.pageSize}
              currentPage={tableQueryResult?.paging.current_page}
              // _toggleActive={_toggleActive}
            />
          </div>
          <div className="hidden sm:block">
            <EventsTable
              data={tableQueryResult?.data}
              pageSize={tableQueryResult?.pageSize}
              currentPage={tableQueryResult?.paging.current_page}
              isLoading={isLoading}
              _handleFilterChange={isFetching ? () => {} : (key, value) => _handleFilterChange(key, value)}
              currentFilterValues={currentFilterValues}
            />
          </div>

          {tableQueryResult?.paging  && tableQueryResult.paging.total_page > 1 ? (
            <div className="mt-6">
              <Pagination 
                hideTotal
                currentPage={tableQueryResult?.paging.current_page}
                totalPage={tableQueryResult?.paging.total_page}
                total={{
                  totalItems: tableQueryResult?.paging.total_item || 0,
                  total: tableQueryResult?.paging.total
                }}
                onChange={(page: number) => _handleFilterChange("page", page)}
              />
            </div>
          ) : <></>}
        </div>
      </div>
      <div className="box-container p-0">
        <div className="border-b border-gray-200 py-4 px-4 sm:px-6">
          <h3 className="text-base font-semibold leading-6 mb-4">
            Sale & Ticket <TooltipCircle text="An overview of Gross Revenue, Net Revenue, and Ticket Quantity for all events currently in the system, regardless of Organization. The data will be calculated based on successful orders and the time of successful order recording." />
          </h3>
          <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-y-4 gap-x-2 md:gap-x-4">
            <div className="flex-1">
              <Label id="chart_gateway_owner" name="Gateway Owner" />
              <Select 
                className="py-0.5"
                options={GATEWAY_OWNER_OPTIONS}
                id="chart_gateway_owner"
                placeholder="Select Gateway Owner"
                value={GATEWAY_OWNER_OPTIONS.find((selected) => selected.value === currentFilterValues.chart_gateway_owner)}
                onChange={(selected: {label: string; value: string}) => {
                  _handleFilterChange("chart_gateway_owner", selected.value);
                  _handleFilterChange("chart_organization_id", "all");
                  _handleFilterChange("chart_gateway_partner_code", "");
                  _handleFilterChange("chart_gateway_name", "");
                  _handleFilterChange("chart_ticket_platform", "");
                }}
              />
            </div>
            <div className="flex-1">
              <Label id="chart_gateway_partner_code" name="Gateway Partner Code" />
              <Select 
                key={`chart_gateway_partner_code__${currentFilterValues.chart_gateway_owner}__${currentFilterValues.chart_organization_id}__${currentFilterValues.chart_ticket_platform}`}
                className="py-0.5"
                options={chartPartnerCodeOptions}
                id="chart_gateway_partner_code"
                placeholder="Select Partner Code"
                value={chartPartnerCodeOptions.find((selected) => selected.value === currentFilterValues.chart_gateway_partner_code)}
                onChange={(selected: {label: string; value: string}) => {
                  _handleFilterChange("chart_gateway_partner_code", selected.value);
                }}
              />
            </div>
            <div className="flex-1">
              <Label id="chart_gateway_name" name="Gateway Name" />
              <Select 
                key={`chart_gateway_name__${currentFilterValues.chart_gateway_owner}__${currentFilterValues.chart_organization_id}__${currentFilterValues.chart_ticket_platform}`}
                className="py-0.5"
                options={chartGatewayNameOptions}
                id="chart_gateway_name"
                placeholder="Select Gateway Name"
                value={chartGatewayNameOptions.find((selected) => selected.value === currentFilterValues.chart_gateway_name)}
                onChange={(selected: {label: string; value: string}) => {
                  _handleFilterChange("chart_gateway_name", selected.value);
                }}
              />
            </div>

            <div className="flex-1">
              <Label id="chart_org" name="Organization" />
              <Select 
                className="py-0.5"
                options={chartOrganizationIds}
                id="chart_org"
                placeholder="Select Organization"
                value={chartOrganizationIds.find((selected: {label: string; value: string}) => selected.value === currentFilterValues.chart_organization_id)}
                onChange={(selected: {label: string; value: string}) => {
                  _handleFilterChange("chart_organization_id", selected.value);
                  _handleFilterChange("chart_gateway_partner_code", "");
                  _handleFilterChange("chart_gateway_name", "");
                }}
              />
            </div>
            {(currentFilterValues.chart_gateway_owner === "all" || currentFilterValues.chart_gateway_owner === "actiup") && (
              <div className="flex-1">
                <Label id="chart_ticket_platform" name="Ticket Platform" />
                <Select 
                  className="py-0.5"
                  options={ticketPlatformList}
                  id="chart_ticket_platform"
                  placeholder="Select Ticket Platform"
                  value={ticketPlatformList.find((selected: {label: string; value: string}) => selected.value === currentFilterValues.chart_ticket_platform)}
                  onChange={(selected: {label: string; value: string}) => {
                    _handleFilterChange("chart_ticket_platform", selected.value);
                    _handleFilterChange("chart_gateway_partner_code", "");
                    _handleFilterChange("chart_gateway_name", "");
                  }}
                />
              </div>
            )}
            <div className="flex-1 bg-white rounded-md">
              <Label id="chart_date" name="Date Range" />
              <ChartDatePickerInput 
                defaultOption={currentFilterValues.chart_date_type as DatePickerFilterOption}
                defaultDate={currentFilterValues.chart_date_value as string}
                _handleDateChange={_handleChartDateChange}
              />
            </div>
          </div>
        </div>
        <div className="box-container">
          <SaleAndTicketChart currentFilterValues={currentFilterValues} />
        </div>
      </div>
      {isLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
}
